import { useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { pushKeyLayer, type NavKey } from '@/lib/keys.ts';
import { isTv } from '@/lib/native.ts';
import { usePhone } from '@/lib/viewport.ts';
import { Keyboard } from '@/ui/Keyboard.tsx';
import { IconSearch } from '@/ui/icons.tsx';
import type { SearchHit } from '@shared/types.ts';
import { api } from '@/lib/api.ts';
import { useSearch } from '@/lib/queries.ts';
import { Focusable } from '@/ui/Focusable.tsx';
import { StatusBadge } from '@/ui/StatusBadge.tsx';
import { player } from '@/lib/player.ts';
import { useUi } from '@/stores/ui.ts';

/**
 * One box, three kinds of answer.
 *
 * Things you can play, then channels, then things you could ask for — in that
 * order, always. Somebody looking for a film does not know whether the house
 * has it, and should not have to decide which part of the app to open based
 * on an answer they do not have yet. Requesting is not a feature you navigate
 * to here; it is what the screen offers when the answer is no.
 *
 * This is also the real answer to a five thousand film library. No amount of
 * scrolling a grid competes with typing four letters, which is why search
 * earns a permanent place in the navigation and the sort dialog does not.
 */
export function SearchPage() {
  /*
   * THE QUERY LIVES IN THE URL, not in component state.
   *
   * It used to be state, and it cost you the search every time you looked at
   * something: opening a film pushes a route, which unmounts this page, and
   * coming back mounted a fresh one with an empty box. You had found the
   * thing, glanced at it, and had to type it again.
   *
   * The URL is where a search query belongs anyway — it makes the results
   * shareable and bookmarkable — but the reason it is here is that react
   * router's history restores it for free, and the query cache is keyed on
   * the same string, so coming back re-renders the previous results with no
   * request at all.
   */
  const [params, setParams] = useSearchParams();
  const q = params.get('q') ?? '';

  /*
   * `text` is what the box shows; `q` is what has been searched for. They are
   * separate because the URL must not be rewritten on every keystroke, and
   * initialised from the URL so arriving back here restores what you typed.
   */
  const [text, setText] = useState(q);
  const box = useRef<HTMLInputElement>(null);
  const navigate = useNavigate();
  const setView = useUi((s) => s.setView);
  const tv = isTv();
  /*
   * The field lives HERE on a television and on a phone, and in the
   * navigation bar everywhere else — see NavSearch. Two screens, two reasons,
   * one rule: whoever can hold the field owns the URL, and only one of them
   * ever does.
   */
  const phone = usePhone();
  const compact = tv || phone;

  /*
   * Debounced, because each keystroke otherwise costs a round trip to Plex
   * AND one to Seerr. 300ms is about the gap between characters when somebody
   * is actually typing, so it fires when they pause rather than as they go.
   */
  useEffect(() => {
    /*
     * A TELEVISION ONLY. In a browser the field lives in the navigation bar
     * and that is what writes the URL; doing it here as well would be two
     * writers for one string, racing each other by a debounce apiece.
     */
    if (!compact) return;
    if (text === q) return;
    const t = setTimeout(() => {
      /*
       * REPLACE, never push. Pushing would put one history entry behind every
       * character typed, so leaving the screen would mean pressing BACK once
       * per letter — the exact failure this change exists to fix, in a new
       * costume. One entry for the search screen, holding the latest query.
       */
      setParams(text ? { q: text } : {}, { replace: true });
    }, 300);
    return () => clearTimeout(t);
  }, [text, q, compact, setParams]);

  /*
   * BACK RETURNS TO THE LIBRARY, and without this it left the app entirely.
   *
   * Nothing on this screen claimed the key, so the Android wrapper fell
   * through to its own handling and walked out — from a screen somebody
   * reached by pressing one button, which made search feel like a trapdoor.
   * Every screen that can be entered has to say what leaving it means.
   */
  useEffect(() => pushKeyLayer((k: NavKey) => {
    if (k !== 'back') return false;
    navigate('/library');
    return true;
  }), [navigate]);

  const { data, isFetching } = useSearch(q);
  const results = data?.results ?? [];
  const channels = data?.channels ?? [];
  const nothing = !!data && !results.length && !channels.length;

  return (
    /*
     * SIDE BY SIDE ON A TELEVISION, stacked in a browser.
     *
     * A keyboard tall enough to use with a D-pad is most of a 16:9 screen, so
     * stacking it above the results means typing, then travelling down past
     * thirty keys to find out what you typed found. Putting the keys in a
     * column on the left leaves the whole right side for answers that update
     * as you go — which is the thing that makes searching on a remote feel
     * like searching rather than data entry.
     */
    <div className={tv ? 'search search-split' : 'search'}>
      {/*
        THE FIELD IS ONLY HERE ON A TELEVISION.

        In a browser it lives in the navigation bar, where it is on every
        screen and never has to be navigated to — and where it is the only
        copy, which matters more: two boxes holding the same string is two
        chances for them to disagree.

        Here it is a read-only display of what the on-screen keys have typed.
        `readOnly` rather than a div so it looks and reads like the field it
        is, and so the WebView never tries to open an IME that will not come.
      */}
      {compact && (
        <div className="search-entry">
          <div className="search-field">
            <IconSearch size={18} />
            {/*
              `readOnly` ONLY on the television, where this is a display of
              what the on-screen keys have typed. On a phone it is an ordinary
              field and the system keyboard is exactly what is wanted — which
              is also why it takes focus on arrival there: somebody who tapped
              a magnifier means to start typing.
            */}
            <input
              ref={box}
              className="search-box"
              type="search"
              value={text}
              readOnly={tv}
              placeholder="Search movies, series & sports"
              aria-label="Search"
              autoFocus={phone && !tv}
              onChange={(e) => setText(e.target.value)}
            />
          </div>

          {tv && (
            <Keyboard
              onKey={(ch) => setText((t) => (t.length >= 60 ? t : t + ch))}
              onDelete={() => setText((t) => t.slice(0, -1))}
              onClear={() => setText('')}
            />
          )}
        </div>
      )}

      <div className="search-answers">
      {q.trim().length >= 2 && (
        <div className="search-status muted">
          {isFetching ? 'Searching…' : nothing ? `Nothing found for “${q}”` : ''}
        </div>
      )}

      {/*
        ABOVE THE ANSWERS, not below them.
        
        This sat under the results, which is exactly where nobody looks: the
        screen appeared to work — a list of films came back — and the only
        sign that half the search had failed was a line of text past the
        bottom of the grid. The symptom reported was "it only searches my
        own library", which is precisely what a silent Seerr failure looks
        like from the sofa. A notice about missing answers has to be where
        the missing answers would have been.
      */}
      {data?.requestError && q.trim().length >= 2 && (
        <div className="search-warn">{data.requestError}</div>
      )}

      {/*
        Said plainly, and before anything is pressed. This profile has no
        account of its own outside the house, so anything it asks for is
        filed under the account that switched into it — and spends that
        person's request allowance. Quiet, not alarming: it is how the thing
        works, not a fault.
      */}
      {data?.requestingAs && q.trim().length >= 2 && (
        <div className="search-note muted">
          Requests from this profile are made as {data.requestingAs}.
        </div>
      )}

      {/*
        ONE GRID. Every match, whether the house has it or not, each saying
        which. The alternative — what you own above a separate list of what
        you do not — makes somebody compare titles across a gap to answer the
        question they came to ask.
      */}
      {results.length > 0 && (
        <div className="res-grid">
          {results.map((hit) => (
            <ResultCard
              key={hit.key}
              hit={hit}
              onPlay={() => navigate(`/library/item/${encodeURIComponent(hit.plexId!)}`)}
              /* A ROUTE NOW, not a dialog. Back works, a reload lands
                 where you were, and the link can be sent to whoever
                 knows whether the 1994 cartoon is the one they meant. */
              onAsk={() => navigate(`/request/${hit.mediaType}/${hit.tmdbId}`)}
            />
          ))}
        </div>
      )}

      {channels.length > 0 && (
        <section className="libe-row">
          <h2 className="libe-title">Channels</h2>
          <div className="search-rows">
            {channels.map((c) => (
              <Focusable
                key={c.id}
                id={`sch-${c.id}`}
                className="btn menu-item"
                onSelect={() => {
                  if (c.kind === 'tv') setView('theater');
                  void player.play(c).catch(() => setView('browse'));
                }}
              >
                {c.name}
                <span className="menu-value">{c.kind === 'tv' ? 'Live TV' : 'Radio'}</span>
              </Focusable>
            ))}
          </div>
        </section>
      )}

      {!q && !tv && (
        <div className="muted center search-hint">
          Anything the library does not have, you can ask for from here.
          <br />
          <Focusable id="search-browse" className="btn menu-item" onSelect={() => navigate('/library')}>
            Browse the library instead
          </Focusable>
        </div>
      )}
      </div>

    </div>
  );
}

/**
 * One result, whether the house has it or not.
 *
 * THE STATE IS SAID ONCE, under the title, where the press target is.
 *
 * It was briefly said twice — a pill on the artwork as well — on the theory
 * that the pill is what you catch scanning and the bar is what you read when
 * deciding. In practice they sat four millimetres apart saying the same word,
 * and the second one does not help you scan, it just makes you check whether
 * it says something different. The Movie/TV badge stays because it is the one
 * thing on the card said nowhere else.
 *
 * What pressing does follows from the same fact. Something in the library
 * opens it; anything else opens the confirming step. Nothing requests on a
 * single press: searching "spiderman" returns the 2002 film, a 1994 cartoon
 * and a 1977 series, and somebody meaning the film asked the server to
 * download the cartoon — and only found out when it arrived.
 */
function ResultCard({
  hit, onPlay, onAsk,
}: {
  hit: SearchHit;
  onPlay(): void;
  onAsk(): void;
}) {
  const inLibrary = !!hit.plexId && (hit.state === 'available' || hit.tmdbId === null);

  return (
    <Focusable
      id={`res-${hit.key}`}
      className="res-card"
      onSelect={() => (inLibrary ? onPlay() : onAsk())}
      backdrop={hit.posterUrl}
    >
      <div className="res-art">
        {hit.posterUrl
          ? <img src={hit.posterUrl} alt="" loading="lazy" decoding="async" />
          : <span className="res-noart">{hit.title.slice(0, 1)}</span>}

        {/* Film or series. Its absence is what let a 1994 cartoon be
            requested by somebody meaning the 2002 film. */}
        <span className="res-kind">{hit.mediaType === 'tv' ? 'TV' : 'Movie'}</span>
        <StatusBadge state={hit.state} />

        {/*
          WHAT IT IS, ON THE ARTWORK, ONCE THE CURSOR IS ON IT.
          
          A grid of posters answers "which one" and nothing else — for
          anything you have not seen, the artwork is a guess. Putting the
          year, the title and two lines of synopsis under the cursor answers
          "what is this" without costing a press or a screen, and it costs
          nothing at all for the twenty cards you are not looking at.
        */}
        <div className="res-overlay">
          {hit.year && <div className="res-ov-year">{hit.year}</div>}
          <div className="res-ov-title">{hit.title}</div>
          {hit.overview && <p className="res-ov-text">{hit.overview}</p>}
        </div>
      </div>

    </Focusable>
  );
}
